import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FrontofficeDeeplinkResolve200ContentRType } from 'generated/api/frontofficeAPI.schemas';

import { useQuickCategoryListAnalytics } from 'hooks/useQuickCategoryListAnalytics';

import { generateCategoryLink } from '@Modules/categories/utils';
import { selectLanguage, selectSearch } from '@Modules/app/selectors';
import { selectRelatedCategoriesInPickerOrder } from '@Modules/filters/selectors';

import type { CategoryLinkItem } from './CategoryLinksList';
import { CategoryLinksList } from './CategoryLinksList';

type CategoryLinksProps = {
  onCategoryClick?: (categoryId: number) => void;
};

export const CategoryLinks = ({ onCategoryClick }: CategoryLinksProps = {}) => {
  const lang = useSelector(selectLanguage);
  const categories = useSelector(selectRelatedCategoriesInPickerOrder);
  const searchParams = useSelector(selectSearch);
  const { trackChipClick, trackShowMoreClick } = useQuickCategoryListAnalytics({ kind: 'strip' });

  const { items, idsKey } = useMemo(() => {
    const list = categories.map<CategoryLinkItem>((category) => ({
      id: category.id,
      key: category.key,
      title: category.title,
      count: category.count,
      to: generateCategoryLink({ uri: category.webUriInfo[lang], searchParams }),
    }));

    // A different strip (other ids) remounts the list and drops its expanded state.
    return { items: list, idsKey: list.map((item) => item.id).join(',') };
  }, [categories, lang, searchParams]);

  if (items.length === 0) {
    return null;
  }

  return (
    <CategoryLinksList
      key={idsKey}
      items={items}
      onItemClick={(item, { position, isExpanded, hadToggle }) =>
        trackChipClick({
          route_type: FrontofficeDeeplinkResolve200ContentRType.CATEGORY,
          id: item.id,
          position,
          is_expanded: isExpanded,
          is_expandable: hadToggle,
        })
      }
      onItemSelect={onCategoryClick}
      onShowMoreClick={({ visibleCount, totalCount }) =>
        trackShowMoreClick({
          categories_visible_count: visibleCount,
          categories_total_count: totalCount,
        })
      }
    />
  );
};
